import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Product } from '../types';

// Helpers
const toNum = (v: any): number => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? Number(n) : 0;
};
const toInt = (v: any): number => {
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return Number.isFinite(n) ? Number(n) : 0;
};

// Transform Firebase doc to Product
const transformFirebaseProduct = (docId: string, data: any): Product => {
  const price = toNum(data.price || 0);
  const originalPrice = data.originalPrice != null ? toNum(data.originalPrice) : price;
  const stock = toInt(data.stock || 0);

  const discount = originalPrice > price ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;
  const finalPrice = price;

  // Dimensions: accept object or string; keep compatibility
  let dimensions: any = data.dimensions;
  if (data.dimensions && typeof data.dimensions === 'object') {
    const { length, width, height } = data.dimensions;
    const l = toNum(length);
    const w = toNum(width);
    const h = toNum(height);
    if (l > 0 || w > 0 || h > 0) {
      dimensions = { length: l || undefined, width: w || undefined, height: h || undefined };
    }
  }

  const images = Array.isArray(data.images) ? data.images.filter((x: any) => typeof x === 'string' && x) : [];
  const primaryImage = images[0] || data.image || '';
  const thumbnail = data.thumbnail || primaryImage;

  const categories = Array.isArray(data.categories) ? data.categories : [];
  const category = data.category || categories[0] || '';
  const categoryNames = Array.isArray(data.categoryNames) ? data.categoryNames : [];

  const features = Array.isArray(data.features) ? data.features : [];
  const tags = Array.isArray(data.tags) ? data.tags : [];
  const deliverablePincodes = Array.isArray(data.deliverablePincodes) ? data.deliverablePincodes : [];

  const rating = data.rating || data.averageRating || 0;
  const numRatings = data.reviews || data.totalRatings || data.numRatings || 0;

  const createdAt = data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : (data.createdAt || new Date().toISOString());
  const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : (data.updatedAt || new Date().toISOString());

  // Variant collections
  const variantColors = Array.isArray(data.variantColors)
    ? data.variantColors.map((c: any) => ({
        name: String(c?.name || ''),
        hex: String(c?.hex || '#cccccc'),
        images: Array.isArray(c?.images) ? c.images.filter((u: any) => typeof u === 'string' && u) : [],
        slug: String((c?.name || '')).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
      }))
    : [];

  const variantSizes = Array.isArray(data.variantSizes)
    ? data.variantSizes.map((s: any) => ({
        label: String(s?.label || ''),
        dimensions:
          s?.dimensions && typeof s.dimensions === 'object'
            ? {
                length: s.dimensions.length != null ? toNum(s.dimensions.length) : undefined,
                width: s.dimensions.width != null ? toNum(s.dimensions.width) : undefined,
                height: s.dimensions.height != null ? toNum(s.dimensions.height) : undefined,
              }
            : undefined,
      }))
    : [];

  const variants = Array.isArray(data.variants)
    ? data.variants.map((v: any) => {
        const colorName = String(v?.color || '');
        const sizeLabel = String(v?.size || '');
        const colorHex =
          v?.colorHex ||
          variantColors.find((c) => c.name.toLowerCase() === colorName.toLowerCase())?.hex ||
          undefined;
        const id =
          v?.id ||
          `${sizeLabel}__${colorName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return {
          id,
          size: sizeLabel,
          color: colorName,
          colorHex,
          priceModifier: v?.priceModifier != null ? toNum(v.priceModifier) : 0,
          stock: v?.stock != null ? toInt(v.stock) : 0,
          sku: v?.sku || '',
          isActive: v?.isActive !== false,
          images: Array.isArray(v?.images) ? v.images.filter((u: any) => typeof u === 'string' && u) : undefined,
        };
      })
    : [];

  const base: any = {
    id: docId,
    name: data.name || '',
    slug: data.slug || '',
    description: data.description || '',
    price,
    originalPrice,
    discount,
    finalPrice,
    stock,
    image: primaryImage,
    images,
    thumbnail,
    category,
    categories,
    categoryNames,
    categoryPath: data.categoryPath || '',
    subcategory: data.subcategory || '',
    rating,
    averageRating: rating,
    totalRatings: numRatings,
    reviews: numRatings,
    numRatings: numRatings,
    brand: data.brand || '',
    material: data.material || '',
    dimensions, // keep as object or whatever your UI expects
    weight: data.weight || '',
    warranty: data.warranty || '',
    features,
    tags,
    deliverablePincodes,
    pincodes: deliverablePincodes,
    isFeatured: Boolean(data.isFeatured),
    isNewArrival: Boolean(data.isNewArrival),
    isBestSeller: Boolean(data.isBestSeller),
    isActive: data.isActive !== undefined ? Boolean(data.isActive) : true,
    createdAt,
    updatedAt,
    adminId: data.adminId || '',
    engagement: data.engagement || {
      views: 0,
      wishlistCount: 0,
      cartCount: 0,
      orders: 0,
    },
    // Variants
    variantColors,
    variantSizes,
    variants,
    hasVariants: variants.length > 0,
    specs: data.specs || {},
    ...(data.ratings && { ratings: data.ratings }),
  };

  // Include any additional fields that don't collide
  const skipFields = new Set([
    'id','name','slug','description','price','originalPrice','stock','image','images','thumbnail',
    'category','categories','categoryNames','categoryPath','subcategory','rating','averageRating',
    'totalRatings','reviews','numRatings','brand','material','dimensions','weight','warranty',
    'features','tags','deliverablePincodes','pincodes','isFeatured','isNewArrival','isBestSeller',
    'isActive','createdAt','updatedAt','adminId','engagement','variants','variantColors','variantSizes',
    'hasVariants','specs','ratings','finalPrice','discount'
  ]);

  for (const key of Object.keys(data)) {
    if (!skipFields.has(key)) {
      base[key] = data[key];
    }
  }

  return base as Product;
};

export const productService = {
  async getProducts(lastDoc?: DocumentSnapshot, pageSize: number = 12): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> {
    try {
      let q = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));

      return {
        products,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      };
    } catch (error) {
      console.error('Error getting products:', error);
      throw error;
    }
  },

  async getProductsByCategory(category: string, lastDoc?: DocumentSnapshot, pageSize: number = 12): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> {
    try {
      let q = query(
        collection(db, 'products'),
        where('category', '==', category),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      let snapshot = await getDocs(q);
      let products = snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));

      if (products.length === 0) {
        q = query(
          collection(db, 'products'),
          where('categories', 'array-contains', category),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          firestoreLimit(pageSize)
        );

        if (lastDoc) {
          q = query(q, startAfter(lastDoc));
        }

        snapshot = await getDocs(q);
        products = snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
      }

      return {
        products,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      };
    } catch (error) {
      console.error('Error getting products by category:', error);
      throw error;
    }
  },

  async getProductById(productId: string): Promise<Product | null> {
    try {
      if (!productId) {
        console.error('Product ID is required');
        return null;
      }

      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return transformFirebaseProduct(docSnap.id, docSnap.data());
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting product by ID:', error);
      return null;
    }
  },

  async getProductBySlug(slug: string): Promise<Product | null> {
    try {
      if (!slug) {
        console.error('Product slug is required');
        return null;
      }

      const q = query(
        collection(db, 'products'),
        where('slug', '==', slug),
        where('isActive', '==', true),
        firestoreLimit(1)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        return transformFirebaseProduct(docSnap.id, docSnap.data());
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting product by slug:', error);
      return null;
    }
  },

  async getProductByIdOrSlug(identifier: string): Promise<Product | null> {
    try {
      if (!identifier) return null;

      let product = await this.getProductById(identifier);
      if (!product && identifier.includes('-')) {
        product = await this.getProductBySlug(identifier);
      }
      return product;
    } catch (error) {
      console.error('Error getting product by ID or slug:', error);
      return null;
    }
  },

  async rateProduct(productId: string, userId: string, rating: number, review?: string): Promise<void> {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) throw new Error('Product not found');

      const data = productSnap.data() || {};
      const ratings = data.ratings || {};
      const oldRating = ratings?.[userId]?.rating || 0;
      const currentTotal = data.totalRatings || data.reviews || 0;
      const currentAverage = data.averageRating || data.rating || 0;

      const newTotalRatings = currentTotal + (oldRating ? 0 : 1);
      const newAverageRating =
        newTotalRatings > 0
          ? (currentAverage * currentTotal - oldRating + rating) / newTotalRatings
          : rating;

      await updateDoc(productRef, {
        [`ratings.${userId}`]: {
          rating,
          review,
          createdAt: new Date().toISOString(),
        },
        averageRating: newAverageRating,
        totalRatings: newTotalRatings,
        rating: newAverageRating,
        reviews: newTotalRatings,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error rating product:', error);
      throw error;
    }
  },

  async getUserRating(productId: string, userId: string): Promise<{ rating: number; review?: string } | null> {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) return null;

      const data = productSnap.data() || {};
      const userRating = data.ratings?.[userId] || null;
      return userRating;
    } catch (error) {
      console.error('Error getting user rating:', error);
      throw error;
    }
  },

  async getTopRatedProducts(limitCount: number = 8): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        orderBy('rating', 'desc'),
        firestoreLimit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting top rated products:', error);
      throw error;
    }
  },

  async getFeaturedProducts(limitCount: number = 8): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('isFeatured', '==', true),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting featured products:', error);
      throw error;
    }
  },

  async getNewArrivals(limitCount: number = 8): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('isNewArrival', '==', true),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting new arrivals:', error);
      throw error;
    }
  },

  async getBestSellers(limitCount: number = 8): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('isBestSeller', '==', true),
        where('isActive', '==', true),
        orderBy('rating', 'desc'),
        firestoreLimit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting best sellers:', error);
      throw error;
    }
  },

  async updateProductCarouselStatus(
    productId: string,
    updates: {
      isFeatured?: boolean;
      isNewArrival?: boolean;
      isBestSeller?: boolean;
    }
  ): Promise<void> {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating product carousel status:', error);
      throw error;
    }
  },

  async getAllProductsForAdmin(): Promise<Product[]> {
    try {
      const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));
    } catch (error) {
      console.error('Error getting all products for admin:', error);
      throw error;
    }
  },

  async searchProducts(searchTerm: string, limitCount: number = 20): Promise<Product[]> {
    try {
      const q = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        firestoreLimit(limitCount * 2)
      );

      const snapshot = await getDocs(q);
      const allProducts = snapshot.docs.map(doc => transformFirebaseProduct(doc.id, doc.data()));

      const term = searchTerm.toLowerCase().trim();
      const includes = (text?: string) => (text ? text.toLowerCase().includes(term) : false);
      const arrIncludes = (arr?: string[]) => (Array.isArray(arr) ? arr.some(includes) : false);

      const filtered = allProducts.filter((p: any) => {
        return (
          includes(p.name) ||
          includes(p.description) ||
          arrIncludes(p.categories) ||
          includes(p.category) ||
          arrIncludes(p.tags) ||
          includes(p.material) ||
          arrIncludes(p.features)
        );
      });

      return filtered.slice(0, limitCount);
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  async debugProduct(productId: string): Promise<void> {
    try {
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        console.log('Raw Firebase data:', docSnap.data());
        const transformed = transformFirebaseProduct(docSnap.id, docSnap.data());
        console.log('Transformed product:', transformed);
      } else {
        console.log('Product does not exist');
      }
    } catch (error) {
      console.error('Error in debug:', error);
    }
  }
};